import clsx from 'clsx';
import type { Swiper } from 'swiper/types';
import type { KeyboardMethods } from 'swiper/types/modules/keyboard';
import { useSwiper } from 'swiper/react';
import { Button } from '@marriott/mi-ui-library';
import { CarouselSwiper, CarouselSlideWrapper } from './Carousel.styles';
import { CarouselBaseProps, CarouselButtonProps, CarouselSlideProps } from './Carousel.types';
import { getClickTrackValue } from '../../utils/getClickTrackValue';
import { useCarouselContext } from './Carousel.context';
import { useViewportCategory } from '../../utils/hooks/useViewportCategory';
import 'swiper/css';
import 'swiper/css/pagination';

const nonFocusableSlideA11yKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'];

export function CarouselSlide({ children, className, onClick, shouldSetIsInitialized }: CarouselSlideProps) {
  const { isInitialized } = useCarouselContext();

  return (
    <CarouselSlideWrapper
      className={clsx(className, shouldSetIsInitialized && !isInitialized && 'swiper-slide-active')} // always apply active class to first slide for server render
      onClick={onClick}
    >
      {children}
    </CarouselSlideWrapper>
  );
}

export const CarouselButton = ({
  className,
  direction,
  onClick,
  trackingProperties,
  trackingPropertyFallbacks,
  tabIndex,
}: CarouselButtonProps) => {
  const swiper = useSwiper();
  const isPrevious = direction === 'left';
  const arrowClassName = isPrevious ? 'carousel-left-arrow' : 'carousel-right-arrow';
  const customClickTrackValue = getClickTrackValue({
    trackingProperties,
    fallbacks: trackingPropertyFallbacks,
  });

  return (
    <Button
      callback={() => {
        onClick?.();
        isPrevious ? swiper.slidePrev() : swiper.slideNext();
      }}
      className={clsx(className, arrowClassName)}
      custom_click_track_value={customClickTrackValue}
      testId={arrowClassName}
      ariaLabel={arrowClassName}
      tabIndex={tabIndex}
      trackingProperties={trackingProperties}
    >
      <span className={isPrevious ? 'icon-arrow-left' : 'icon-arrow-right'} />
    </Button>
  );
};

export function CarouselBase({ children, className, options, onTouchEnd, onSwiper }: CarouselBaseProps) {
  const { setSwiperTouched, setIsInitialized, swiperRef, setProgress } = useCarouselContext();
  const { modules = [] } = options || {};
  const viewportCategory = useViewportCategory(); // This is added to rerender the carousel when width changes - there is a bug that can show when pagination dots need to be added and are not previously there.

  return (
    <CarouselSwiper
      onTouchEnd={(...touchEndArguments: [swiper: Swiper, event: MouseEvent | TouchEvent | PointerEvent]) => {
        setSwiperTouched(() => true);
        onTouchEnd?.(...touchEndArguments);
      }}
      onSwiper={(swiper: Swiper) => {
        swiperRef.current = swiper;
        setIsInitialized(() => true);
        onSwiper?.();
      }}
      onProgress={(_: Swiper, progress: number) => {
        setProgress(() => progress);
      }}
      slidesPerView="auto"
      centeredSlides
      modules={[...modules]}
      keyboard={{ enabled: true, onlyInViewport: true }} // Note: defaults for keyboard
      className={className}
      key={viewportCategory} // forces re-render on viewport change
      onSlideChange={(swiper: Swiper) => {
        const activeSlide = swiper.slides[swiper.activeIndex];
        const firstFocusableElement = activeSlide.querySelector<HTMLElement>(
          'button, a, input, [tabindex]:not([tabindex="-1"])'
        );
        const keyboardMeta = swiper.keyboard as KeyboardMethods & { lastKey: string | null };
        const lastKey = keyboardMeta?.lastKey;

        if (!nonFocusableSlideA11yKeys.includes(lastKey || '')) {
          firstFocusableElement?.focus();
        }

        if (lastKey) {
          keyboardMeta.lastKey = null;
        }
      }}
      {...options}
    >
      {children}
    </CarouselSwiper>
  );
}
